// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Link from "@conklin/MobiusOverrides/Link/Link";
import { EMPTY_VALUE, sharedDateTimeOptions } from "@conklin/XDL/constants";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { InvoiceModel } from "@insite/client-framework/Types/ApiModels";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableCell } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import React, { FC } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    invoice: InvoiceModel;
    extendedStyles?: InvoiceSummaryCardStyles;
    payInvoiceLink?: string;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface InvoiceSummaryCardStyles {
    invoiceSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    invoiceNumberGridItem?: GridItemProps;
    invoiceNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webInvoiceNumberGridItem?: GridItemProps;
    webInvoiceNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    clickableCell?: LinkPresentationProps;
    dataCells?: DataTableCellProps;
    actionsCells?: DataTableCellProps;
    payLink?: LinkPresentationProps;
}

export const invoiceSummaryCardStyles: InvoiceSummaryCardStyles = {};

const InvoiceSummaryCard: FC<Props> = ({ language, invoice, extendedStyles, payInvoiceLink }) => {
    if (!invoice) {
        return null;
    }
    const [styles] = React.useState(() => mergeToNew(invoiceSummaryCardStyles, extendedStyles));
    const invoiceNumber = invoice.invoiceNumber;
    const invoiceDateDisplay = getLocalizedDateTime({
        dateTime: new Date(invoice.invoiceDate),
        language,
        options: sharedDateTimeOptions,
    });

    const dueDateDisplay = getLocalizedDateTime({
        dateTime: new Date(invoice.dueDate),
        language,
        options: sharedDateTimeOptions,
    });

    return (
        <>
            <DataTableCell {...{ header: translate("Invoice #") }}>
                <InvoiceDetailPageTypeLink title={invoiceNumber} invoiceNumber={invoiceNumber} />
            </DataTableCell>
            <DataTableCell {...{ header: translate("PO #") }}>{invoice.customerPO || EMPTY_VALUE}</DataTableCell>
            <DataTableCell {...{ header: translate("Invoice Date") }} {...styles.dataCells}>
                {invoiceDateDisplay || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...{ header: translate("Due Date") }} {...styles.dataCells}>
                {dueDateDisplay || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...{ header: translate("Total") }} {...styles.dataCells}>
                {invoice.invoiceTotalDisplay || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...{ header: translate("Balance") }} {...styles.dataCells}>
                {invoice.currentBalanceDisplay || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...{ header: translate("Pay Invoice") }} {...styles.dataCells}>
                <Link href={payInvoiceLink} target="_blank" {...styles.payLink}>
                    {translate("Pay Invoice")}
                </Link>
            </DataTableCell>
        </>
    );
};

export default connect(mapStateToProps)(InvoiceSummaryCard);
